import {Formik, Form, FormikProps} from 'formik';
import {forEach, map} from 'lodash';
import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {userApi} from 'api';
import {FormControls, PasswordInputFormik, Button, notifier, PredefinedNotifierType} from 'components';
import {PROFILE_FIELD} from 'constants/user';
import {ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError} from 'types';
import {apiUtils} from 'utils';

type Props = {
    cancelButton?: ReactNode;
    onSuccess?: () => void;
}

type FormFields = {
    oldPassword: string;
    password: string;
    confirmPassword: string;
}

const PROFILE_T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.PROFILE;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;
const T_KEYS = TRANSLATION_KEYS.COMPONENTS.CHANGE_PASSWORD;

const OVERRIDES = {
    [PROFILE_FIELD.OLD_PASSWORD]: {
        [ValidationCode.Invalid]: PROFILE_T_KEYS.ERRORS.OLD_PASSWORD.INVALID
    },
    [PROFILE_FIELD.PASSWORD]: {
        [ValidationCode.Duplicated]: PROFILE_T_KEYS.ERRORS.PASSWORD.DUPLICATE,
        [ValidationCode.InvalidFormat]: PROFILE_T_KEYS.ERRORS.PASSWORD.INVALID_FORMAT,
        [ValidationCode.MinLength]: PROFILE_T_KEYS.ERRORS.PASSWORD.MIN_LENGTH,
        [ValidationCode.MaxLength]: PROFILE_T_KEYS.ERRORS.PASSWORD.MAX_LENGTH,
    },
    [PROFILE_FIELD.CONFIRM_PASSWORD]: {
        [ValidationCode.MinLength]: PROFILE_T_KEYS.ERRORS.PASSWORD.MIN_LENGTH,
        [ValidationCode.Invalid]: PROFILE_T_KEYS.ERRORS.CONFIRM_PASSWORD.INVALID
    }
};

type FormTemplateProps = {
    error: ApiError;
    loading: boolean;
    passwordChanged: boolean;
    cancelButton?: ReactNode;
} & FormikProps<FormFields>

const FormTemplate: React.FC<FormTemplateProps> = ({cancelButton, values, loading, passwordChanged, error, setFieldError}) => {
    const {t} = useTranslation();

    const isValid = useCallback(() => values.oldPassword && values.password && values.confirmPassword, [values]);

    useEffect(() => {
        const err = apiUtils.getFieldsValidationErrorMessages(error, t, map(PROFILE_FIELD, (value) => ({
            field: value,
            overrides: OVERRIDES[value]
        })));
        forEach(err, (value, key) => value && setFieldError(key, value));
    }, [t, setFieldError, error]);

    return (
        <Form>
            <FormControls>
                <PasswordInputFormik name='oldPassword' label={t(PROFILE_T_KEYS.FORM_CONTROLS.OLD_PASSWORD)} required />
                <PasswordInputFormik name='password' label={t(COMMON_T_KEYS.FORM_CONTROL.PASSWORD)} required />
                <PasswordInputFormik name='confirmPassword' label={t(COMMON_T_KEYS.FORM_CONTROL.CONFIRM_PASSWORD)} required />
            </FormControls>
            <div className='tw-mt-6 tw-text-center'>
                {cancelButton}
                <Button
                    className='tw-mb-2 tw-mx-1'
                    color='primary'
                    disabled={!isValid() || loading || passwordChanged}
                    type='submit'
                >
                    {t(COMMON_T_KEYS.BUTTONS.OK)}
                </Button>
            </div>
        </Form>
    );
};

export const ChangePassword: React.FC<Props> = ({cancelButton, onSuccess}) => {
    const {t} = useTranslation();
    const [passwordChanged, setPasswordChanged] = useState(false);

    const initialValues: FormFields = useMemo(() => ({
        oldPassword: '',
        password: '',
        confirmPassword: ''
    }), []);

    const handleSuccess = useCallback(() => {
        notifier.success({title: t(T_KEYS.NOTIFIER.SUCCESS)});
        setPasswordChanged(true);
        onSuccess?.();
    }, [t, onSuccess]);

    const {send: updatePassword, loading, error} = useApiData(userApi.updateUserPassword, {
        onSuccess: handleSuccess, notificationType: PredefinedNotifierType.SaveError
    });

    const handleSubmit = useCallback((data) => {
        updatePassword(data);
    }, [updatePassword]);

    return (
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            {(props) => (
                <FormTemplate {...props} cancelButton={cancelButton} loading={loading} passwordChanged={passwordChanged} error={error} />
            )}
        </Formik>
    );
};